import React, { useEffect, useState } from "react";
import { Download, X, Smartphone, Info } from "lucide-react";

export default function PwaInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    // Already dismissed this session or running as installed PWA
    if (sessionStorage.getItem("pwa-dismissed") === "1") return;
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Always show after 1.5s regardless of prompt availability
    const timer = setTimeout(() => setVisible(true), 1500);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setInstalled(true);
      setVisible(false);
      sessionStorage.setItem("pwa-dismissed", "1");
    });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
        setVisible(false);
      } else {
        setInstalling(false);
      }
      setDeferredPrompt(null);
    } else {
      // No native prompt → show manual install tip
      setShowTip((v) => !v);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    sessionStorage.setItem("pwa-dismissed", "1");
  };

  if (!visible || dismissed || installed) return null;

  return (
    <>
      {/* Floating pill container — fixed right side, vertically centered */}
      <div
        id="pwa-install-btn"
        className="fixed z-[9999]"
        style={{ right: 0, top: "50%", transform: "translateY(-50%)" }}
      >
        {/* Manual install tooltip (shown when native prompt unavailable) */}
        {showTip && (
          <div
            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 w-64 rounded-xl p-4 text-sm shadow-2xl"
            style={{
              background: "linear-gradient(135deg, #0b2b5e, #061830)",
              border: "1px solid rgba(250,204,21,0.3)",
              color: "#e2e8f0",
            }}
          >
            <div className="flex items-center gap-2 mb-2 font-bold text-yellow-300">
              <Info className="h-4 w-4 shrink-0" />
              Cara Install Manual
            </div>
            <ul className="space-y-1 text-xs text-slate-300 leading-relaxed">
              <li>• <strong>Chrome/Edge:</strong> Klik ikon ⋮ lalu <em>"Instal Ditresnarkoba..."</em></li>
              <li>• <strong>Chrome mobile:</strong> Ketuk menu → <em>"Tambahkan ke layar utama"</em></li>
              <li>• <strong>Safari iOS:</strong> Ketuk ⎙ → <em>"Add to Home Screen"</em></li>
            </ul>
            <div
              className="absolute top-1/2 right-[-6px] -translate-y-1/2 h-3 w-3 rotate-45"
              style={{ background: "#0b2b5e", border: "1px solid rgba(250,204,21,0.3)", borderLeft: "none", borderBottom: "none" }}
            />
          </div>
        )}

        {/* Dismiss X */}
        <button
          onClick={handleDismiss}
          aria-label="Tutup"
          className="absolute -top-3 -left-3 z-10 h-6 w-6 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          style={{ background: "rgba(11,43,94,0.9)", border: "1px solid rgba(255,255,255,0.2)" }}
        >
          <X className="h-3 w-3 text-white" />
        </button>

        {/* Main pill */}
        <button
          onClick={handleInstall}
          disabled={installing}
          aria-label="Install aplikasi"
          className="group relative flex flex-col items-center gap-2 px-3 py-5 rounded-l-2xl overflow-hidden transition-all hover:px-4 active:scale-95"
          style={{
            background: "linear-gradient(180deg, #1a4a8a 0%, #0b2b5e 40%, #061830 100%)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRight: "none",
            minWidth: 60,
            boxShadow: "-6px 0 24px rgba(11,43,94,0.6), inset 1px 0 0 rgba(255,255,255,0.08)",
          }}
        >
          {/* Gold accent bar top */}
          <span
            className="absolute top-0 left-3 right-3 h-px rounded-full"
            style={{ background: "linear-gradient(90deg, transparent, #facc15, transparent)" }}
            aria-hidden
          />

          {/* Shimmer sweep */}
          <span
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)",
              backgroundSize: "200% 200%",
              animation: "shimmer 3s linear infinite",
            }}
            aria-hidden
          />

          {/* Icon pill (gold) */}
          <span
            className="relative flex items-center justify-center h-11 w-11 rounded-xl transition-all group-hover:scale-110 group-hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #facc15 0%, #f59e0b 100%)",
              boxShadow: "0 4px 14px rgba(250,204,21,0.45), 0 0 0 3px rgba(250,204,21,0.12)",
            }}
          >
            {installing ? (
              <Smartphone className="h-5 w-5 text-[#0b2b5e] animate-bounce" />
            ) : (
              <Download className="h-5 w-5 text-[#0b2b5e] group-hover:animate-bounce" />
            )}
          </span>

          {/* Label */}
          <span className="relative text-[10px] font-black tracking-[0.15em] uppercase text-white leading-none">
            {installing ? "Loading" : "Install"}
          </span>

          {/* Pulse ring effect */}
          {!installing && (
            <span
              className="absolute inset-0 rounded-l-2xl pointer-events-none"
              style={{ animation: "installPulse 2.5s ease-in-out infinite" }}
              aria-hidden
            />
          )}
        </button>
      </div>

      <style>{`
        @keyframes installPulse {
          0%, 100% { box-shadow: -6px 0 20px rgba(250,204,21,0); }
          50%        { box-shadow: -6px 0 24px rgba(250,204,21,0.25); }
        }
      `}</style>
    </>
  );
}
